import React from 'react';
import { ListItemStyle } from './styles';
import { useStyletron } from '../../Themes';
import { Checkbox } from '../Checkbox';
import { StatefulMenu } from 'baseui/menu';
import { IDummyProps, TItem } from './types';
import { MIcon, NoIcon } from '../Icon';
import * as meta from '../../meta-data.json';
import { focusRing } from '../../Themes/base/focusRing';
import { borderColor, borderRadius, borderWidth } from '../../Utilities/utils/Theming';
import { Button } from '../Button';
import { Popover, StatefulPopover } from '../Popover';
import useOnClickOutside from '../../Utilities/hooks/useOnClickOutside';

const hasListSubItems = (item: any) => Array.isArray(item.subList) && item.subList.length > 0;

export const RenderBasicItemsList = (item: TItem) => {
	const [css] = useStyletron();

	let enhancer;
	if (typeof item.icon == 'string') {
		enhancer = (
			<MIcon
				icon={item.icon}
				size="var(--epods--icon-size-small)"
				color="var(--epods--color-text-primary)"
				title={item.icon}
				className={css({
					marginRight: 'var(--epods--space-inset-s)',
				})}
			/>
		);
	} else if (typeof item.icon == 'boolean' && item.icon === false) {
		enhancer = (
			<NoIcon
				className={css({
					marginRight: 'var(--epods--space-inset-s)',
				})}
			/>
		);
	} else {
		enhancer = item.icon;
	}

	if (!item.id) {
		console.warn(
			"DS components - The list has items without ids. It is recommended to add id on each item.",
		);
	}

	const itemId = item.id ? String(item?.id) : '';

	return (
		<Popover ignoreBoundary isOpen={(!!item.isHighlighted && !!item.tooltip)} content={item.tooltip || ""}>
			<div
				className={css({
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					width: '100%',
				})}
				id={itemId}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						overflow: 'hidden',
						width: '100%',
					}}
				>
					{enhancer}
					<div
						data-epo="dropdown-button-item-list" // Be carefully when you delete this. it breaks the nested list behaviour (recursive lists)
						style={(typeof item.label === 'string' && !item.noEllipsis)
							? {
								textOverflow: 'ellipsis',
								overflow: 'hidden',
								whiteSpace: 'nowrap',
								width: '100%',
							} : {}}
					>
						{item.label}
					</div>
				</div>
				{
					hasListSubItems(item) && (
						<MIcon icon="navigate_next" size="var(--epods--icon-size-small)" title="navigate_next" />
					)
				}
				{
					item.subItemContent && (
						<div onClick={(e) => {
							e.stopPropagation()
						}}>
							<StatefulPopover inverted content={item.subItemContent} placement={item.subItemPosition}>
								<Button />
							</StatefulPopover>
						</div>
					)
				}
			</div>
		</Popover>
	);
};

export const RenderCheckboxItemsList = (item: TItem) => {
	return (
		<Popover isOpen={(!!item.isHighlighted && !!item.tooltip)} content={item.tooltip || ""}>
			<div style={{ display: 'flex', alignItems: 'center' }}>
				<Checkbox aria-label="List Checkbox label" name="test-name" checked={item.checked} 
				overrides={{
					Input: {
						style: {
							display: 'none'
						}
					}
				}}/>
				{item.label}
			</div>
		</Popover>
	);
};

export const RenderChildComponents = ({
	close,
	onItemSelect,
	items: itemsParameter,
	size,
	styleListContainer = {
		width: '300px',
	},
	isCustom,
	onClickOutside,
	onCloseList,
	customItem, 
}: {
	close?: any;
	onItemSelect: any;
	items: any;
	size?: string;
	styleListContainer?: any;
	isCustom?: boolean;
	onClickOutside?: () => void;
	onCloseList?: () => void;
	customItem?: (item: any) => React.ReactNode
}) => {
	/**
	 * We need the 2nd parameter because we don't know how many nested lists will have the dropdown button. 
	 * So, clicking on any item from a list will close cause it is consider onClickOutside for the other list
	 */
	const { ref } = useOnClickOutside(onClickOutside, { epoDataAttribute: 'data-epo', epoDataAttributeValue: 'dropdown-button-item-list' });

	return (
		<div ref={ref}>
			<StatefulMenu
				items={itemsParameter}
				onItemSelect={(item: any) => {
					onCloseList && onCloseList();
					onItemSelect && onItemSelect(item);
				}}
				overrides={{
					List: {
						style: {
							boxShadow: '0 0 0 transparent',
							...borderRadius('var(--epods--border-radius)'),
							...borderColor('var(--epods--color-border-light)'),
							borderStyle: 'solid',
							...borderWidth('var(--epods--border-width)'),
							...styleListContainer,
						},
						props: {
							'data-epo': `epo-list-${meta.version}`,
							part: `epo-list-${meta.version}`,
						},
					},
					Option: {
						props: {
							'data-size': size,
							getItemLabel: (item: any) => {
								return customItem ? customItem(item) : <RenderBasicItemsList {...item} />
							},
							getChildMenu: (item: any) => {
								return item && item?.subList ? (
									<RenderChildComponents
										styleListContainer={styleListContainer}
										close={close}
										size={size}
										onItemSelect={onItemSelect}
										items={item.subList}
										onCloseList={onCloseList}
										onClickOutside={onClickOutside}
										customItem={customItem}
										isCustom={isCustom}
									/>
								) : null;
							},
							overrides: {
								ListItem: {
									style: ({ $theme, $selected, $isHighlighted, $disabled }: any) =>
										ListItemStyle({ $theme, $selected, $isHighlighted, $disabled, size, isCustom }),
								},
							},
						}
					},
				}}
			/>
		</div>
	);
};

const ListItem = ({ $theme, $selected, $isHighlighted, $disabled, size, isCustom, children, ...props }: any) => {
	const [css] = useStyletron();
	return (
		<li {...props}>
			<div className={css(ListItemStyle({ $theme, $selected, $isHighlighted, $disabled, size, isCustom }))}>
				{children}
			</div>
		</li>
	)
}

export const overrides = (props: IDummyProps) => {
	const {
		styleListContainer = {
			width: '300px',
		},
		size = 'default',
		isCustom,
		hideFocusRing = false,
		onCloseList,
		customItem,
		tabIndex = 0,
	} = props;

	const _focus = hideFocusRing
		? { ":focus": { outline: "none" } }
		: { ":focus": { ...focusRing } }

	return {
		List: {
			style: {
				...styleListContainer,
				boxShadow: '0 0 0 transparent',
				..._focus,
				paddingTop: "0",
				paddingBottom: "0",
				...borderRadius('var(--epods--border-radius)'),

			},
			props: {
				'data-epo': `epo-list-${meta.version}`,
				part: `epo-list-${meta.version}`,
				tabIndex,
			},
		},
		Option: {
			props: {
				'data-size': size,
				getItemLabel: (item: any) => {
					return customItem ? customItem(item)
						: props.multiselect
							? (
								<RenderCheckboxItemsList {...item} />
							) : (
								<RenderBasicItemsList {...item} />
							);
				},
				getChildMenu: (item: any) => {
					return item.subList ? (
						<RenderChildComponents
							styleListContainer={styleListContainer}
							onItemSelect={props.onItemSelect}
							items={item.subList}
							size={size}
							onCloseList={onCloseList}
							customItem={customItem}
							isCustom={isCustom}
						/>
					) : null;
				},
				'data-epo': `epo-list-${meta.version}`,
				part: `epo-list-${meta.version}`,
				overrides: {
					ListItem: {
						style: ({ $theme, $selected, $isHighlighted, $disabled, ...args }: any) => {
							return ListItemStyle({ $theme, $selected, $isHighlighted, $disabled, size, isCustom });
						},
					},
					ChildMenuPopover: {
						style: {
							boxShadow: 'none'
						},
					}
				}
			},
		},
	};
};
